import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2, Save, Loader2, FileText, Facebook, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import API from '@/config/api';

const listField = (label, items, setItems, placeholder) => (
  <div>
    <Label className="text-sm font-medium mb-2 block">{label}</Label>
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-2">
          <Input
            value={item}
            onChange={(e) => { const n = [...items]; n[idx] = e.target.value; setItems(n); }}
            placeholder={placeholder}
            data-testid={`${label.toLowerCase().replace(/[^a-z]+/g, '-')}-item-${idx}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setItems(items.filter((_, i) => i !== idx))}
            data-testid={`remove-${label.toLowerCase().replace(/[^a-z]+/g, '-')}-${idx}`}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setItems([...items, ''])}
        data-testid={`add-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
      >
        <Plus className="w-4 h-4 mr-1" />
        Add item
      </Button>
    </div>
  </div>
);

const NewsletterPosterSettings = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/newsletters/poster/defaults`, getAuthHeaders());
        setDefaults(res.data);
      } catch {
        toast.error('Failed to load newsletter defaults');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!defaults) return;
    setSaving(true);
    try {
      // Strip empty items from list fields
      const clean = { ...defaults };
      ['important_reminders', 'what_to_bring_daily', 'program_highlights'].forEach(k => {
        clean[k] = (clean[k] || []).map(s => (s || '').trim()).filter(Boolean);
      });
      clean.use_emojis = !!clean.use_emojis;
      await axios.put(`${API}/newsletters/poster/defaults`, clean, getAuthHeaders());
      toast.success('Newsletter defaults saved!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading newsletter settings…
      </div>
    );
  }
  if (!defaults) return null;

  const setField = (key, value) => setDefaults({ ...defaults, [key]: value });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Newsletter Poster Settings
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          These details appear on every monthly poster newsletter. Fill this out once — you&apos;ll only need to type the welcome message and any special events each month.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Tagline</Label>
            <Input value={defaults.tagline || ''} onChange={(e) => setField('tagline', e.target.value)} placeholder="Discover · Learn · Grow" data-testid="tagline-input" />
          </div>
          <div>
            <Label>Ages served</Label>
            <Input value={defaults.age_range_served || ''} onChange={(e) => setField('age_range_served', e.target.value)} placeholder="6 weeks – 12 years" data-testid="age-range-input" />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-amber-50/40 bg-amber-50/20">
              <input
                type="checkbox"
                checked={!!defaults.use_emojis}
                onChange={(e) => setField('use_emojis', e.target.checked)}
                className="mt-1"
                data-testid="use-emojis-toggle"
              />
              <div>
                <p className="text-sm font-medium">Use color emojis in poster PDF</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Type any emoji at the start of a reminder / checklist item (e.g. <code>💰 Tuition is due…</code> or <code>✅ Wipes</code>) and it will render as a color icon in the printed poster. Leave off for a plain-text look.
                </p>
              </div>
            </label>
          </div>
          <div className="md:col-span-2">
            <Label>Center hours (one line)</Label>
            <Input value={defaults.hours_line || ''} onChange={(e) => setField('hours_line', e.target.value)} placeholder="Monday – Friday: 6:30 AM – 6:00 PM" data-testid="hours-line-input" />
          </div>
          <div className="md:col-span-2">
            <Label>Holidays closed (optional)</Label>
            <Input value={defaults.closed_holidays_note || ''} onChange={(e) => setField('closed_holidays_note', e.target.value)} placeholder="Labor Day, Thanksgiving Day, Christmas Day" data-testid="holidays-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Facebook className="w-4 h-4" /> Facebook page</Label>
            <Input value={defaults.facebook_page || ''} onChange={(e) => setField('facebook_page', e.target.value)} placeholder="Our Center on Facebook" data-testid="facebook-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Instagram className="w-4 h-4" /> Instagram handle</Label>
            <Input value={defaults.instagram_handle || ''} onChange={(e) => setField('instagram_handle', e.target.value)} placeholder="@ourcenter" data-testid="instagram-input" />
          </div>
        </div>

        {listField('Important Reminders', defaults.important_reminders || [], (v) => setField('important_reminders', v), 'e.g. Tuition is due on the 1st of each month')}
        <div>
          <Label>Transportation Reminder</Label>
          <Textarea rows={3} value={defaults.transportation_reminder || ''} onChange={(e) => setField('transportation_reminder', e.target.value)} placeholder="Drop-off / pick-up rules…" data-testid="transportation-input" />
        </div>
        {listField('What to Bring Daily', defaults.what_to_bring_daily || [], (v) => setField('what_to_bring_daily', v), 'e.g. Wipes')}
        {listField('Program Highlights', defaults.program_highlights || [], (v) => setField('program_highlights', v), 'e.g. Age-appropriate curriculum')}

        <div>
          <Label>Footer thank-you line</Label>
          <Input value={defaults.footer_thank_you || ''} onChange={(e) => setField('footer_thank_you', e.target.value)} placeholder="Thank you for trusting us with your most precious gifts!" data-testid="footer-thanks-input" />
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-newsletter-defaults">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default NewsletterPosterSettings;
